import {
  ChannelClosedIcon,
  ChannelIcon,
  ChannelOpenIcon,
  CollectionBoardIcon,
  CollectionTableIcon,
  LockIcon,
  NoteIcon,
  NoteIcon as SvgNoteIcon,
  NoteIconRobotFace,
  RecurringIcon,
} from '@slite/icons'
import classNames from 'classnames'
import React, { CSSProperties, forwardRef } from 'react'
import { ColorMode } from '../ColorMode'
import { useColorMode } from '../ColorMode/hooks/useColorMode'
import { getEmojiIndex } from '../Emoji/data'
import { Emoji } from '../Emoji/Emoji'
import { ICONS_COLORS } from '../EmojiPicker/constants'
import {
  CHANNEL_ICON_SHAPE,
  COLLECTION_BOARD_ICON_SHAPE,
  COLLECTION_ROOT_ICON_SHAPE,
  COLLECTION_TABLE_ICON_SHAPE,
  SUPER_ASSISTANT_ICON_SHAPE,
  SUPER_DIGEST_ICON_SHAPE,
} from './constants'
import { NoteIconVariant, toNoteIconComponent } from './types'
import {
  getSuperSourceIcon,
  isSuperSourceIconShape,
} from './getSuperSourceIcon'

function getColorForIcon(iconColor: string | null, colorMode: ColorMode) {
  const colorProp = ICONS_COLORS.find((c) => c.id === iconColor)
  if (colorProp) {
    return colorMode === ColorMode.Dark ? colorProp.dark : colorProp.light
  }
  return undefined
}

function getIconContent({
  iconShape,
  iconColor,
  iconVariant,
  notionIcon,
  size,
  variant,
  expanded,
  colorMode,
  isCold,
}: {
  iconShape: string | null
  iconColor: string | null
  iconVariant: NoteIconVariant | null
  notionIcon: string | null
  size: React.CSSProperties['height']
  variant: 'small' | 'normal'
  expanded: boolean
  colorMode: ColorMode
  isCold?: boolean
}) {
  if (notionIcon) {
    const isUrl = notionIcon?.startsWith('https://')
    return (
      <span
        className={'flexbox-inline flexbox-centered position-relative'}
        style={{ width: size, height: size, fontSize: size }}
      >
        {isUrl ? (
          // eslint-disable-next-line jsx-a11y/alt-text
          <img src={notionIcon} style={{ width: size, height: size }} />
        ) : (
          notionIcon // if not a URL, it's a utf8
        )}
      </span>
    )
  }
  if (isSuperSourceIconShape(iconShape)) {
    const SuperSourceIcon = getSuperSourceIcon({ iconShape })
    if (SuperSourceIcon) {
      return <SuperSourceIcon size={size} />
    }
  }
  if (iconShape === COLLECTION_ROOT_ICON_SHAPE) {
    return <CollectionTableIcon size={size} />
  }
  if (iconShape === COLLECTION_BOARD_ICON_SHAPE) {
    return <CollectionBoardIcon size={size} />
  }
  if (iconShape === COLLECTION_TABLE_ICON_SHAPE) {
    return <CollectionTableIcon size={size} />
  }
  if (iconShape === CHANNEL_ICON_SHAPE) {
    const isPrivate =
      iconVariant && ['private', 'personal'].includes(iconVariant)
    if (variant === 'small' && isPrivate) {
      return <LockIcon size={size} />
    }
    if (variant === 'small') {
      return <ChannelIcon size={size} />
    }
    if (isPrivate) {
      return <LockIcon size={size} />
    }
    if (expanded) {
      return <ChannelOpenIcon size={size} />
    }
    return <ChannelClosedIcon size={size} />
  }
  if (iconShape === SUPER_ASSISTANT_ICON_SHAPE) {
    return <NoteIconRobotFace size={size} />
  }
  if (iconShape === SUPER_DIGEST_ICON_SHAPE) {
    return <RecurringIcon size={size} />
  }

  // If supported icon
  if (iconShape) {
    const IconComp = toNoteIconComponent(iconShape)
    const color = getColorForIcon(iconColor, colorMode)
    if (IconComp && color) {
      return (
        <IconComp
          size={size}
          style={{ color }}
          className={isCold ? 'opacity-small' : ''}
        />
      )
    }
  }

  // If supported emoji
  const emoji = iconShape && getEmojiIndex()[iconShape]
  if (emoji) {
    return (
      <Emoji
        emoji={emoji}
        size={size}
        className={isCold ? 'opacity-small' : ''}
      />
    )
  }

  const color = getColorForIcon(iconColor, colorMode)
  return variant === 'small' ? (
    <NoteIcon size={size} style={{ color }} />
  ) : (
    <SvgNoteIcon size={size} style={{ color }} />
  )
}

export type CustomNoteIconProps = {
  className?: string
  backgroundClassName?: string
  style?: CSSProperties
  iconShape?: string | null
  iconColor?: string | null
  iconVariant?: NoteIconVariant | null
  notionIcon?: string | null // render icon from Notion sources
  isExpanded?: boolean | null
  size?: React.CSSProperties['height']
  expanded?: boolean
  variant?: 'small' | 'normal'
  isCold?: boolean
}

export const CustomNoteIcon = forwardRef<HTMLElement, CustomNoteIconProps>(
  function CustomNoteIcon(
    {
      iconShape = null,
      iconColor = null,
      iconVariant = null,
      notionIcon = null,
      size = 16,
      variant = 'normal',
      className,
      style,
      expanded = false,
      isCold = false,
    },
    ref
  ) {
    const colorMode = useColorMode()
    return (
      <span
        ref={ref}
        className={classNames(
          className,
          'flexbox-inline flexbox-centered position-relative',
          isCold && 'shade-30'
        )}
        style={{ ...style, width: size, height: size }}
      >
        {getIconContent({
          iconShape,
          iconColor,
          iconVariant,
          notionIcon,
          size,
          variant,
          expanded,
          colorMode,
          isCold,
        })}
      </span>
    )
  }
)
